import type useLuxPlusPricingContext from 'hooks/useLuxPlusPricingContext'

// Taken from the hook's return rather than SPP's util, which is where the named type happens to
// live — a shared loyalty lib should not reach up into offer-page internals for a type.
type LuxPlusPricingContext = ReturnType<typeof useLuxPlusPricingContext>

/**
 * What the member would be charged for a package — LuxPlus+ price when they can genuinely redeem
 * it, regular price otherwise.
 *
 * Callers should feed it dated pricing where any exists — the offer-page modal passes the package
 * view's `hotelTotal`/`hotelMemberTotal`. The raw package's own `price`/`memberPrice` are the offer
 * payload's undated lead-in, acceptable only as a fallback. Checkout prices upgrades from the item
 * view's dated totals via `getItemViewTotalsPrice` instead of this helper.
 *
 * Gated on `canRedeemLuxPlusBenefits`, **not** the display hook `getCanViewLuxPlusBenefits`.
 * Free-preview and pending-renewal members are *shown* member pricing but *charged* the regular
 * price, so using the display hook here would quote a figure they cannot get.
 */
function getLoyaltyUpgradePackagePayablePrice(
  hotelPackage: Pick<App.PackagePrice, 'price' | 'memberPrice'>,
  context: LuxPlusPricingContext,
): number {
  const { price, memberPrice } = hotelPackage
  const paysMemberPrice =
    context.luxPlusEnabled &&
    context.canRedeemLuxPlusBenefits &&
    !context.isGhaPricing &&
    memberPrice > 0 &&
    memberPrice < price

  return paysMemberPrice ? memberPrice : price
}

export default getLoyaltyUpgradePackagePayablePrice
